import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Table,
  Input,
  Select,
  Space,
  Card,
  Typography,
  Spin,
  Empty,
  Tag,
  Tooltip,
  Button,
  Modal,
  Radio,
  Checkbox,
  message,
  InputNumber,
} from 'antd';
import {
  SearchOutlined,
  SettingOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { performSearch, saveSearchHistory } from '@redux/global-search/actions';
import facilityActions from '@redux/facility/actions';
import styled from 'styled-components';
import { EventName, useEventBus } from '@/hooks/useEventBus';
import { useKeepAliveContext } from '@/hooks/use-keep-alive-context';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const SearchResultsContainer = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;
`;

const SearchCard = styled(Card)`
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SearchForm = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const SearchField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
`;

const SearchLabel = styled.label`
  font-weight: 500;
  color: #262626;
  font-size: 14px;
`;

const ResultsCard = styled(Card)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StyledModal = styled(Modal)`
  .ant-checkbox-wrapper + .ant-checkbox-wrapper {
    margin-left: 0;
  }
`;

const SearchResultsTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // 从 URL 参数获取默认值
  const queryText = searchParams.get('q') || '';
  const queryModels = searchParams.get('models') ? searchParams.get('models').split(',') : [];
  const queryScope = searchParams.get('scope') || 'current-facility';
  
  // 获取 facilityId 和 companyId
  const facilityId = useSelector((state) => state.Facility?.selected?.Id);
  const companyId = useSelector((state) => state.Company?.selectedId);
  const facilityOptions = useSelector((state) => state.Facility.data);
  
  // 搜索状态
  const [searchText, setSearchText] = useState(queryText);
  const [scope, setScope] = useState(queryScope);
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [tempScope, setTempScope] = useState(queryScope);
  const [searchLimit, setSearchLimit] = useState(20);
  const [tempSearchLimit, setTempSearchLimit] = useState(20);
  const [tempModels, setTempModels] = useState(
    queryModels.length > 0 ? queryModels : [
      'workorders',
      'equipment',
      'electricity',
      'gas',
      'files',
      'functionaltesting',
      'comments',
      'scenarios',
    ]
  );
  const { closeTab } = useKeepAliveContext();
  useEventBus(EventName.CLOSE_RESULT_SEARCH, () => {
    closeTab('/common/search-result')
  })
  const [tempSelectedModels, setTempSelectedModels] = useState(
    queryModels.length > 0 ? queryModels : [
    'workorders',
    'equipment',
    'electricity',
    'gas',
    'files',
    'functionaltesting',
    'comments',
    'scenarios',
    ]
  );

  // 从Redux获取状态
  const loading = useSelector((state) => state.GlobalSearch?.loading || false);
  const searchResults = useSelector((state) => state.GlobalSearch?.results || {});
  const searchHistory = useSelector((state) => state.GlobalSearch?.history || []);

  // 搜索范围选项
  const scopeOptions = [
    { value: 'current-facility', label: 'Current Facility (Default)' },
    { value: 'current-company', label: 'Current Company' },
    { value: 'all-facilities', label: 'All Facilities' },
  ];

  // 搜索模块选项
  const modelOptions = [
    { value: 'workorders', label: 'Work Orders' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'electricity', label: 'Electricity Data' },
    { value: 'gas', label: 'Gas Data' },
    { value: 'files', label: 'Files' },
    { value: 'functionaltesting', label: 'Functional Tests' },
    { value: 'comments', label: 'Comments' },
    { value: 'scenarios', label: 'Scenarios' },
  ];

  // 处理搜索
  const handleSearch = () => {
    if (searchText.trim()) {
      // 保存搜索历史
      dispatch(saveSearchHistory(searchText, tempSelectedModels));
      
      // 执行搜索
      dispatch(performSearch(searchText, tempSelectedModels, facilityId, companyId, scope, searchLimit));
      
      console.log('Search Keywords:', searchText);
      console.log('Selected Models:', tempSelectedModels);
      console.log('Search Scope:', scope);
      console.log('Search Limit:', searchLimit);
      console.log('Facility ID:', facilityId);
      console.log('Company ID:', companyId);
    }
  };

  // 处理回车键搜索
  const handlePressEnter = () => {
    handleSearch();
  };

  // 处理设置按钮点击
  const handleSettingsClick = () => {
    setTempScope(scope);
    setTempModels([...tempSelectedModels]);
    setTempSearchLimit(searchLimit);
    setIsSettingsModalVisible(true);
  };

  // 处理设置保存
  const handleSettingsSave = () => {
    setScope(tempScope);
    setTempSelectedModels([...tempModels]);
    setSearchLimit(tempSearchLimit);
    setIsSettingsModalVisible(false);
  };

  // 处理设置取消
  const handleSettingsCancel = () => {
    setIsSettingsModalVisible(false);
  };

  // 处理全选
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setTempModels(modelOptions.map(option => option.value));
    } else {
      setTempModels([]);
    }
  };

  // 处理清空
  const handleClearAll = () => {
    setTempModels([]);
  };

  // 组件加载时自动执行搜索（如果有查询参数）
  useEffect(() => {
    if (queryText.trim()) {
      console.log('Auto-executing search with URL params:', {
        query: queryText,
        models: queryModels,
        scope: queryScope,
        facilityId: facilityId,
        companyId: companyId
      });
      
      // 执行搜索
      dispatch(performSearch(queryText, queryModels.length > 0 ? queryModels : tempSelectedModels, facilityId, companyId, queryScope, searchLimit));
    }
  }, []); // 只在组件挂载时执行一次



  // Handle item click - 参考 search-modal.js 的逻辑
  const handleItemClick = (category, item) => {
    const isClickabledCategory = category === "Workorders" || category === "Equipment" || category === "Files" || category === "FunctionalTesting";

    if(!isClickabledCategory) {
      return;
    }

    // 检查是否需要切换设施
    const currentFacilityId = facilityId;
    const itemFacilityId = item.facility_id;
    
    // 导航函数
    const navigateToItem = () => {
      // Navigate to different pages based on the category
      switch (category) {
        case "Workorders":
          navigate(`/facility/search/workorder?id=${item.id}`);
          break;
        case "Equipment":
          navigate(`/facility/search/equipment?id=${item.id}`);
          break;
        case "Files":

            navigate(`/facility/search/file-manage?url=${item.remark}`);
          break;
        case "FunctionalTesting":
          navigate(`/facility/search/functional-testing?id=${item.id}`);
          break;
        default:
          navigate(`/facility/Dashboard`);
      }
    };

    // 如果是同一个设施或者没有设施信息，直接导航
    if (currentFacilityId === itemFacilityId) {
      navigateToItem();
      return;
    }

    // 如果是不同设施，显示确认弹窗
    Modal.confirm({
      title: 'Switch Facility',
      content: 'This search result is from another facility, do you want to switch from current facility?',
      okText: 'Yes, Switch',
      cancelText: 'Cancel',
      zIndex: 9999,
      onOk: () => {
        navigateToItem();
        setTimeout(() => {
          // 切换facility - 参考 SelectFacility.js 的数据结构
          console.log('准备切换设施:', {
            itemFacilityId,
            currentFacilityId
          });
          
          // 从现有的设施数据中查找匹配的设施
          const targetFacility = facilityOptions.find(facility => 
            facility.record && String(facility.record.id) === String(itemFacilityId)
          );
          
          let facilityOption;
          if (targetFacility) {
            // 如果找到了匹配的设施，使用完整的设施数据
            facilityOption = {
              Id: targetFacility.record.id,
              value: targetFacility.record.name,
              label: targetFacility.record.name,
              record: targetFacility.record
            };
            console.log('使用完整设施数据切换到:', facilityOption.value);
          } else {
            // 如果没有找到，使用搜索结果中的信息构建
            facilityOption = {
              Id: itemFacilityId,
              value: item.facility_name || item.facility || `Facility ${itemFacilityId}`,
              label: item.facility_name || item.facility || `Facility ${itemFacilityId}`,
              record: {
                id: itemFacilityId,
                name: item.facility_name || item.facility || `Facility ${itemFacilityId}`,
                company: item.company_name || item.company || 'Unknown Company',
                // 可以根据搜索结果中的其他字段添加更多属性
                ...item
              }
            };
            console.log('使用搜索结果构建设施切换到:', facilityOption.value);
          }
          
          dispatch(facilityActions.SelectedFacility(facilityOption));
          console.log('设施切换 action 已派发');
        }, 100);
      },
      onCancel: () => {
        // 用户取消，不做任何操作
        console.log('用户取消设施切换');
      }
    });
  };

  // 获取项目类型
  const getItemType = (category, item) => {
    switch (category) {
      case 'Workorders':
        return item.status || 'Work Order';
      case 'Equipment':
        return item.category || 'Equipment';
      case 'Files':
        return 'File';
      case 'Electricity':
        return 'Electricity';
      case 'Gas':
        return 'Gas';
      case 'FunctionalTesting':
        return 'Test';
      case 'WorkorderComments':
        return 'Comment';
      case 'Scenarios':
        return 'Scenario';
      default:
        return '';
    }
  };

  // 获取项目标题
  const getItemTitle = (category, item) => {
    switch (category) {
      case 'Workorders':
        return item.title || item.number || `Work Order #${item.id}`;
      case 'Equipment':
        return item.name || `Equipment #${item.id}`;
      case 'Files':
        return item.name || `File #${item.id}`;
      case 'Electricity':
      case 'Gas':
        return `${item.type} - ${item.service_month}`;
      case 'FunctionalTesting':
        return item.description || `Test #${item.id}`;
      case 'WorkorderComments':
        return item.comment || item.description || `#${item.id}`;
      case 'Scenarios':
        return item.name || `Scenario #${item.id}`;
      default:
        return `#${item.id}`;
    }
  };

  // 获取项目详情
  const getItemDetails = (category, item) => {
    switch (category) {
      case 'Workorders':
        return (
          <Space>
            <span>#{item.number || item.id}</span>
            {item.date && <span>• {item.date}</span>}
          </Space>
        );
      case 'Equipment':
        return (
          <Space>
            <span>#{item.id}</span>
            {item.modified_date && <span>• {item.modified_date}</span>}
          </Space>
        );
      case 'Files':
        return item.folder || item.modified_date;
      case 'Electricity':
      case 'Gas':
        return (
          <Space>
            <span>${item.costs?.toFixed(2)}</span>
            <span>• {item.usage}</span>
          </Space>
        );
      default:
        return item.modified_date || null;
    }
  };

  // 构建表格数据
  const buildTableData = () => {
    const data = [];
    
    Object.entries(searchResults).forEach(([category, categoryData]) => {
      // 适配新的数据格式: { limit, records, total }
      const items = categoryData?.records || categoryData;
      const total = categoryData?.total;
      const limit = categoryData?.limit;
      
      if (items && items.length > 0) {
        // 如果有 total 信息，显示在 description 中
        const description = total && total > items.length
          ? `Showing ${items.length} of ${total} results`
          : `${items.length} result${items.length > 1 ? 's' : ''}`;
        
        const tableCategoryData = {
          key: category,
          category: category,
          subject: category,
          type: 'Category',
          description: description,
          notes: '',
          facility: '',
          children: items.map((item, index) => ({
            key: `${category}-${index}`,
            category: category,
            subject: item.subject || '-',
            type: item.type || '-',
            description: item.description || '-',
            notes: item.notes || '-',
            facility: item.facility || '-',
            rawData: item,
          })),
        };
        data.push(tableCategoryData);
      }
    });
    
    return data;
  };

  // 表格列定义
  const columns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (text, record) => {
        if (record.children) {
          return <strong>{text}</strong>;
        }
        return null; // 子行不显示 category
      },
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        if (record.children) {
          return <strong>{text}</strong>;
        }
        return (
          <Tooltip title={text}>
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {text}
            </div>
          </Tooltip>
        );
      },
    },

    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        if (record.children) {
          return text; // Category 行显示数量
        }
        // 移除 HTML 标签用于显示
        const cleanText = text?.replace(/<[^>]*>/g, '') || '-';
        return (
          <Tooltip title={cleanText}>
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {cleanText}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        if (record.children) {
          return null; // Category 行不显示 type
        }
        return (
          <Tooltip title={text}>
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        if (record.children) {
          return null;
        }
        return (
          <Tooltip title={text}>
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Facility',
      dataIndex: 'facility',
      key: 'facility',
      width: 150,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        if (record.children) {
          return null;
        }
        // Only show View button for Workorders, Equipment, and Files
        const allowedCategories = ['Workorders', 'Equipment', 'Files'];
        if (!allowedCategories.includes(record.category)) {
          return null;
        }
        return (
          <Button 
            type="link" 
            size="small"
            onClick={() => handleItemClick(record.category, record.rawData)}
          >
            View
          </Button>
        );
      },
    },
  ];

  // 检查是否有搜索结果
  const hasResults = Object.values(searchResults).some((data) => {
    // 适配新格式: { limit, records, total } 或旧格式: []
    const records = data?.records || data;
    return records && records.length > 0;
  });

  return (
    <>
      <StyledModal
        title="Search Settings"
        open={isSettingsModalVisible}
        onOk={handleSettingsSave}
        onCancel={handleSettingsCancel}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ marginBottom: 16, fontSize: '16px', fontWeight: '600', color: '#262626' }}>
            Select scope of search
          </h4>
          
          <Radio.Group value={tempScope} onChange={(e) => setTempScope(e.target.value)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Radio value="current-facility">Current facility (Default)</Radio>
              <Radio value="current-company">Current Company</Radio>
              <Radio value="all-facilities">All facilities</Radio>
            </div>
          </Radio.Group>
        </div>

        <h4 style={{ marginBottom: 16, fontSize: '16px', fontWeight: '600', color: '#262626' }}>
          Select data Category to search
        </h4>
        
        {/* Select All / Clear All controls */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
          <Checkbox
            checked={tempModels.length === modelOptions.length}
            indeterminate={tempModels.length > 0 && tempModels.length < modelOptions.length}
            onChange={handleSelectAll}
          >
            Select All
          </Checkbox>
          <Checkbox
            checked={tempModels.length === 0}
            onChange={handleClearAll}
          >
            Clear All
          </Checkbox>
        </div>

        <Checkbox.Group
          value={tempModels}
          onChange={setTempModels}
          style={{ width: '100%' }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 16px' }}>
            <Checkbox value="workorders">Work Orders</Checkbox>
            <Checkbox value="equipment">Equipment</Checkbox>
            <Checkbox value="electricity">Electricity Data</Checkbox>
            <Checkbox value="gas">Gas Data</Checkbox>
            <Checkbox value="files">Files</Checkbox>
            <Checkbox value="functionaltesting">Functional Tests</Checkbox>
            <Checkbox value="comments">Comments</Checkbox>
            <Checkbox value="scenarios">Scenarios</Checkbox>
          </div>
        </Checkbox.Group>

        <h4 style={{ marginTop: 20, marginBottom: 16, fontSize: '16px', fontWeight: '600', color: '#262626' }}>
          Search limit
        </h4>
        
        <div style={{ marginBottom: 16 }}>
          <InputNumber
            min={20}
            max={50}
            value={tempSearchLimit}
            onChange={(value) => setTempSearchLimit(value || 20)}
            style={{ width: '100%' }}
            placeholder="Enter search limit (20-50)"
          />
          <div style={{ marginTop: 8, fontSize: '12px', color: '#6b7280' }}>
            Number of results per data category (20-50)
          </div>
        </div>
      </StyledModal>

      <SearchResultsContainer>
      <SearchCard>
        
        <SearchForm>
          <SearchField>
            <SearchLabel>Search Keywords</SearchLabel>
            <Search
              placeholder="Enter search keywords..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handlePressEnter}
              onSearch={handleSearch}
              enterButton={<SearchOutlined />}
              size="large"
            />
          </SearchField>

          <SearchField>
            <SearchLabel>Search Settings</SearchLabel>
            <Button
              icon={<SettingOutlined />}
              onClick={handleSettingsClick}
              size="large"
              style={{ width: '100%' }}
            >
              Configure Search Options
            </Button>
          </SearchField>
        </SearchForm>
      </SearchCard>

      <ResultsCard>
        <Title level={5} style={{ marginBottom: 16 }}>
          Search Results
        </Title>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Searching...</div>
          </div>
        ) : !searchText ? (
          <Empty
            description="Enter search keywords to start searching"
            style={{ margin: '40px 0' }}
          />
        ) : !hasResults ? (
          <Empty
            description="No results found"
            style={{ margin: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={buildTableData()}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            expandable={{
              defaultExpandAllRows: true,
            }}
            size="middle"
            bordered
            scroll={{ x: 1200 }}
          />
        )}
      </ResultsCard>
    </SearchResultsContainer>
    </>
  );
};

export default SearchResultsTable; 